import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Box, Button, CloseButton, Dialog, Field, Heading, Input, Portal, Show, Table, VStack,} from "@chakra-ui/react"
import {useToast} from "@chakra-ui/toast";
import {CompanyExecutor, dataApi, ExecutorContract as ExContract} from "../../api/data-api";
import {DateTime} from "luxon";
import {Tooltip} from "../../components/Tooltip";
import {DocumentFile} from "../Document/documentFile";
import ExecutorTask from "./executorTask";

export interface ExecutorContractExt {
    id: number | null,
    name: string,
    endDate: string,
    documentId: number | null,
    sended: boolean,
}

export default function ExecutorContract(props: {
    executor: CompanyExecutor | undefined,
    isCompany: boolean,
    show: boolean
    setShow?: React.Dispatch<React.SetStateAction<boolean>>
}) {
    const inputDefault: ExecutorContractExt = useMemo(() => {
        return {
            id: null,
            name: "",
            endDate: "",
            documentId: null,
            sended: false,
        }
    }, []);
    const [inputParams, setInputParams] = useState<ExecutorContractExt>(inputDefault);
    const [items, setItems] = useState<ExContract[]>([]);
    const [curItem, setCurItem] = useState<ExContract>();
    const toast = useToast();
    const [showForm, setShowForm] = useState<boolean>(false);
    const [showTasks, setShowTasks] = useState<boolean>(false);
    const loadItems = useCallback(() => {
        if (props.executor) {
            dataApi.executorContractList(props.executor.ID)
                .then((o) => setItems(o))
                .catch((err) => {
                    console.log("Error status code is: " + err);
                    toast({title: "Error", description: `code: [${err}]`, status: "error"});
                });
        }
    }, [props, toast]);
    useEffect(() => {
        loadItems();
    }, [loadItems, props, toast]);

    const prepareInput = useCallback((item: ExContract | null) => {
        setInputParams({
            id: item ? item.ID : null,
            name: item ? item.NAME : "",
            endDate: item ? DateTime.fromMillis(item.END_DATE).toFormat('dd.MM.yyyy') : "",
            documentId: item ? item.DOCUMENT_ID : null,
            sended: item ? !!item.SENDED || !!item.CONFIRMED : false,
        });
    }, []);

    const add = useCallback(() => {
        if (!props.executor || !props.executor.ID)
            return;
        dataApi.executorContractAdd(props.executor.ID, inputParams.name, DateTime.fromFormat(inputParams.endDate, 'dd.MM.yyyy').toMillis())
            .then((value) => {
                toast({title: "Add", status: "success"});
                setItems(prev => {
                    const lst = [...prev];
                    lst.push(value);
                    return lst;
                });
                prepareInput(null);
                setShowForm(false);
            })
            .catch((err) => {
                console.log("Error status code is: " + err);
                toast({title: "Error", description: `code: [${err}]`, status: "error"});
            });
    }, [props.executor, inputParams, toast, prepareInput]);

    const save = useCallback(() => {
        if (inputParams.id == null)
            return;
        dataApi.executorContractSave(inputParams.id, inputParams.name, DateTime.fromFormat(inputParams.endDate, 'dd.MM.yyyy').toMillis())
            .then((value) => {
                toast({title: "Save", status: "success"});
                setItems(prev => {
                    const lst = [...prev];
                    const v = lst.find(o => o.ID === inputParams.id);
                    if (v) {
                        v.NAME = inputParams.name;
                        v.END_DATE = DateTime.fromFormat(inputParams.endDate, 'dd.MM.yyyy').toMillis();
                    }
                    return lst;
                });
                prepareInput(null);
                setShowForm(false);
            })
            .catch((err) => {
                console.log("Error status code is: " + err);
                toast({title: "Error", description: `code: [${err}]`, status: "error"});
            });
    }, [toast, inputParams, prepareInput]);

    const send = useCallback((item: ExContract) => {
        if (!item)
            return;
        dataApi.executorContractSend(item.ID)
            .then((o) => {
                if (o) {
                    toast({title: "Confirm", status: "success"});
                    item.SENDED = new Date().getTime();
                    setItems(prev => {
                        const lst = [...prev];
                        let index = lst.indexOf(item);
                        if (index > -1)
                            lst[index] = item;
                        return lst;
                    });
                } else {
                    toast({title: "Confirm failed", status: "error"});
                }
            })
            .catch((err) => {
                console.log("Error status code is: " + err);
                toast({title: "Error", description: `code: [${err}]`, status: "error"});
            });
    }, [toast]);

    const showTaskList = useCallback((item: ExContract | undefined) => {
        setShowTasks(prev => {
            const v = !(prev && curItem && item && curItem.ID === item.ID);
            if (v) {
                setCurItem(item);
            } else {
                setCurItem(undefined);
            }
            return v;
        });
        setShowForm(false);
    }, [curItem]);

    const htmlContent = (<VStack>
        <ExecutorTask contract={curItem} executor={props.executor} show={showTasks} isCompany={props.isCompany}/>
        <Show when={showForm}>
            <Heading size="xl">Контракт {inputParams.id}</Heading>
            <Box borderWidth={1} borderColor="gray">
                <Field.Root orientation="horizontal">
                    <Field.Label>Имя</Field.Label>
                    <Input type="text" placeholder="Имя"
                           value={inputParams.name}
                           onChange={(it) => setInputParams((prevState) => {
                               return {...prevState, name: it.target.value}
                           })}/>
                </Field.Root>
                <Field.Root orientation="horizontal">
                    <Tooltip showArrow content="date, format dd.mm.yyyy">
                        <Field.Label>Дата окончания <Field.RequiredIndicator/></Field.Label>
                    </Tooltip>
                    <Input type="text" placeholder="Дата окончания"
                           value={inputParams.endDate}
                           onChange={(it) => setInputParams((prevState) => {
                               return {...prevState, endDate: it.target.value}
                           })}/>
                </Field.Root>
                <Show when={props.isCompany}>
                    <Button borderRadius={0} variant="solid" colorScheme="teal" width="full"
                            disabled={inputParams.sended}
                            onClick={e => {
                                if (inputParams.id) {
                                    save();
                                } else {
                                    add();
                                }
                            }}>
                        {inputParams.id ? "Сохранить" : "Создать"}
                    </Button>
                </Show>
            </Box>
            {inputParams.id && inputParams.documentId ? (<DocumentFile documentId={inputParams.documentId} editable={true}/>) : null}
        </Show>
        <Show when={props.setShow != null}>
            <Heading size="xl">Контракты</Heading>
        </Show>
        <Show when={props.isCompany}>
            <Button onClick={e => {
                if (showForm) {
                    setShowForm(false);
                } else {
                    prepareInput(null);
                    setShowForm(true);
                }
            }}>
                Создать новый
            </Button>
        </Show>
        <Table.Root size="sm" variant="outline" striped>
            <Table.Header>
                <Table.Row>
                    <Table.ColumnHeader>Имя</Table.ColumnHeader>
                    <Table.ColumnHeader>Дата окончания</Table.ColumnHeader>
                    <Table.ColumnHeader>Отправлен</Table.ColumnHeader>
                    <Table.ColumnHeader>Подтвержден</Table.ColumnHeader>
                    <Table.ColumnHeader>Изменен</Table.ColumnHeader>
                    <Table.ColumnHeader>Действия</Table.ColumnHeader>
                </Table.Row>
            </Table.Header>
            <Table.Body>
                {items.map((item) => (
                    <Table.Row key={item.ID}>
                        <Table.Cell>{item.NAME}</Table.Cell>
                        <Table.Cell>{item.END_DATE ? DateTime.fromMillis(item.END_DATE).toFormat('dd.MM.yyyy') : ""}</Table.Cell>
                        <Table.Cell>{item.SENDED ? DateTime.fromMillis(item.SENDED).toFormat('HH:mm:ss dd.MM.yyyy') : ""}</Table.Cell>
                        <Table.Cell>{item.CONFIRMED ? DateTime.fromMillis(item.CONFIRMED).toFormat('HH:mm:ss dd.MM.yyyy') : ""}</Table.Cell>
                        <Table.Cell>{item.VERSION ? DateTime.fromMillis(item.VERSION).toFormat('HH:mm:ss dd.MM.yyyy') : ""}</Table.Cell>
                        <Table.Cell>
                            <Button onClick={e => {
                                if (showForm && inputParams && inputParams.id === item.ID) {
                                    prepareInput(null);
                                    setShowForm(false);
                                } else {
                                    prepareInput(item);
                                    setShowForm(true);
                                }
                                setShowTasks(false);
                            }}>{!item.SENDED && !item.CONFIRMED ? "Изменить" : "Показать"}</Button>
                            <Button onClick={e => showTaskList(item)}>Задачи</Button>
                            <Show when={!item.SENDED && !item.CONFIRMED && props.isCompany}>
                                <Button
                                    // display={item.CONFIRMED ? "none" : "block"}
                                    onClick={e => send(item)}>Отправить</Button>
                            </Show>
                        </Table.Cell>
                    </Table.Row>
                ))}
            </Table.Body>
        </Table.Root>
    </VStack>);

    return props.setShow ?
        <Dialog.Root size={"xl"} open={props.show} onOpenChange={(v: any) => {
            if (props.setShow)
                props.setShow(v.open);
        }}>
            <Dialog.Trigger/>
            <Portal>
                <Dialog.Backdrop/>
                <Dialog.Positioner>
                    <Dialog.Content>
                        <Dialog.Header>
                            <Dialog.Title>Контракты</Dialog.Title>
                        </Dialog.Header>
                        <Dialog.Body>
                            {htmlContent}
                        </Dialog.Body>
                        <Dialog.Footer/>
                        <Dialog.CloseTrigger asChild>
                            <CloseButton size="sm" onClick={e => {
                                if (props.setShow)
                                    props.setShow(false);
                            }}/>
                        </Dialog.CloseTrigger>
                    </Dialog.Content>
                </Dialog.Positioner>
            </Portal>
        </Dialog.Root> :
        htmlContent;
}